'use client'

// libraries
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'

// components
import Link from '/react4xp/components/Link'
import NotificationBar from '/react4xp/components/NotificationBar'

// css
import './index.scss'

// utils
import { getFocusableElements, getFocusableElementsOutside } from '/react4xp/utils/functions'

// animations
import { useStaggerUp } from '/react4xp/components/Animations/useStaggerUp'
import { useStaggerScale } from '/react4xp/components/Animations/useStaggerScale'
import { useStaggerLeft } from '/react4xp/components/Animations/useStaggerLeft'

// types
interface Props {
    notificationBar?: {
        text: string
        enabled: boolean
    }
    logo?: {
        src: string
        url: string
        alt: string
    }
    menu?: {
        label: string
        url: string
    }[]
    language?: {
        active: 'nb' | 'en'
        noUrl: string
        enUrl: string
        hidden?: boolean
    }
    search?: {
        label: string
        url: string
        icon: string
    }
    menuButton?: {
        open: string
        close: string
    }
    fsMenu?: {
        title: {
            label: string
            url?: string
        }
        links: {
            label: string
            url: string
        }[]
    }[]
    fsMenuLastLink?: {
        label: string
        url: string
    }
    fsMenuIcon?: string
}

export default function Header({
    notificationBar = {
        text: 'This is a notification bar text lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.',
        enabled: true
    },
    logo = {
        src: '/img/logo.png',
        url: '/',
        alt: 'NLA Høgskolen'
    },
    menu = [
        {
            label: 'Studier',
            url: '#'
        },
        {
            label: 'Forskning',
            url: '#'
        },
        {
            label: 'Om NLA',
            url: '#'
        }
    ],
    language = {
        active: 'nb',
        noUrl: '#',
        enUrl: '#'
    },
    search = {
        label: 'Søk',
        url: '#',
        icon: '/svg/ux/magnifier.svg'
    },
    menuButton = {
        open: 'Meny',
        close: 'Lukk'
    },
    fsMenu = [
        {
            title: {
                label: 'Studier',
                url: '#'
            },
            links: [
                {
                    label: 'Søk utdanning',
                    url: '#'
                },
                {
                    label: 'Nettstudier',
                    url: '#'
                },
                {
                    label: 'Videreutdanning',
                    url: '#'
                },
                {
                    label: 'Utveksling',
                    url: '#'
                }
            ]
        },
        {
            title: {
                label: 'For studenter',
                url: '#'
            },
            links: [
                {
                    label: 'Alt om studiestart',
                    url: '#'
                },
                {
                    label: 'Verktøy for studenter',
                    url: '#'
                },
                {
                    label: 'Dine studier',
                    url: '#'
                },
                {
                    label: 'Praksis',
                    url: '#'
                },
                {
                    label: 'Biblioteket',
                    url: '#'
                },
                {
                    label: 'IT-hjelp',
                    url: '#'
                }
            ]
        },
        {
            title: {
                label: 'Studere ved NLA',
                url: '#'
            },
            links: [
                {
                    label: 'Våre studiesteder',
                    url: '#'
                },
                {
                    label: 'Hvorfor velge NLA',
                    url: '#'
                },
                {
                    label: 'Livet rundt studiene',
                    url: '#'
                },
                {
                    label: 'Våre tidligere studenter',
                    url: '#'
                },
                {
                    label: 'Deltidsjobb',
                    url: '#'
                }
            ]
        },
        {
            title: {
                label: 'Om NLA',
                url: '#'
            },
            links: [
                {
                    label: 'Historie og særpreg',
                    url: '#'
                },
                {
                    label: 'Finn ansatte',
                    url: '#'
                },
                {
                    label: 'Organisasjon',
                    url: '#'
                },
                {
                    label: 'Vedtekter og planer',
                    url: '#'
                },
                {
                    label: 'For presse',
                    url: '#'
                },
                {
                    label: 'Støtt NLA',
                    url: '#'
                }

            ]
        },
        {
            title: {
                label: 'Forskning og utvikling',
                url: '#'
            },
            links: [
                {
                    label: 'Finn en ekspert',
                    url: '#'
                },
                {
                    label: 'Publikasjoner',
                    url: '#'
                },
                {
                    label: 'Forskningsgrupper',
                    url: '#'
                },
                {
                    label: 'Prosjekter',
                    url: '#'
                }
            ]
        }
    ],
    fsMenuLastLink = {
        label: 'IT-hjelp',
        url: '#'
    },
    fsMenuIcon = '/svg/ux/chevron-down.svg'
}: Props) {

    // animation ref
    const menuAnimationRef = useRef<GSAPTimeline | null>(null)

    // menu toggle / close button ref (kept focusable while menu is open)
    const menuButtonRef = useRef<HTMLButtonElement>(null)

    // open / close fs menu
    const [isShown, setIsShown] = useState(false)
    const [isTransparent, setIsTransparent] = useState(false)
    const [isAnimating, setIsAnimating] = useState(false)
    const scrollPositionRef = useRef(0)
    const [isMenuHidden, setIsMenuHidden] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)
    const lastScrollYRef = useRef(0)
    const isRestoringScrollRef = useRef(false)
    const hashScrolledRef = useRef(false)

	const openCloseMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        // Prevent toggling if animation is in progress
        if (isAnimating) return
        setIsShown(!isShown)
	}

    // menu animation
    useGSAP(() => {

        const menuAnimation = gsap.timeline({
            paused: true
        })

        menuAnimation.to('[data-fs-menu]', {
            opacity: 1,
            visibility: 'visible',
            ease: 'none',
            duration: 0
        })

        menuAnimation.to('[data-fs-menu]', {
            clipPath: 'inset(0% 0% 0% 0%)',
            ease: 'power2.inOut',
            duration: .6
        })
    
        menuAnimation.fromTo('[data-fs-menu-column]', {
            y: 100,
            autoAlpha: 0
        }, {
            y: 0,
            autoAlpha: 1,
            stagger: .05
        }, '-=.5')

        menuAnimation.fromTo('.fs-menu__last-link', {
            y: 100,
            autoAlpha: 0
        }, {
            y: 0,
            autoAlpha: 1,
        }, '-=.5')
        
        // store the animation timeline in the ref
        menuAnimationRef.current = menuAnimation
    })

    useEffect(() => {

        // notification bar
        const notificationBarEl = document?.querySelector('[data-notification-bar]')
        const notificationBarHeight = notificationBarEl?.clientHeight

		setIsShown(isShown)

        const focusable_els = document.querySelector('[data-fs-menu]') as HTMLElement
        // keep the menu toggle/close button reachable so it isn't disabled below
        const focusableElementsOutside = getFocusableElementsOutside(focusable_els)
            .filter((el) => el !== menuButtonRef.current)

        // disable focus on all elements outside the fs-menu when it's open
        const disableFocusOutside = () => {
            focusableElementsOutside.forEach((el) => {
                el.setAttribute('tabindex', '-1')
            })
        }

        // enable focus on all elements outside the fs-menu when it's closed
        const enableFocusOutside = () => {
            focusableElementsOutside.forEach((el) => {
                el.removeAttribute('tabindex')
            })
        }

        // wrap-around focus cycle: close button -> menu links -> close button
        const handleMenuTabKey = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return

            const trapEls = [menuButtonRef.current, ...Array.from(getFocusableElements(focusable_els))]
                .filter((el): el is HTMLElement => !!el && (el as HTMLElement).offsetParent !== null)

            if (trapEls.length === 0) return

            const first = trapEls[0]
            const last = trapEls[trapEls.length - 1]

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault()
                first.focus()
            }
        }

        if (menuAnimationRef.current) {
            setIsAnimating(true)
            
            if (isShown) {
                // store current scroll position
                scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
                
                menuAnimationRef.current.play()
                document.body.style.overflowY = 'scroll'
                document.body.style.position = 'fixed'
                document.body.style.top = `-${scrollPositionRef.current}px`
                document.body.style.width = '100%'
                disableFocusOutside()
                document.addEventListener('keydown', handleMenuTabKey)

                // if notification bar exists, move the menu up a bit
                if (notificationBarEl && notificationBarHeight) {
                    gsap.to('[data-top-menu]', {
                        marginTop: notificationBarHeight * -1,
                        duration: .6,
                        ease: 'power2.inOut'
                    })
                }

                setTimeout(() => {
                    setIsTransparent(true)
                }, 100)
            } else {
                // restore scroll position
                const scrollPosition = scrollPositionRef.current
                
                menuAnimationRef.current.reverse()
                document.body.style.overflowY = 'auto'
                document.body.style.position = 'relative'
                document.body.style.top = ''
                document.body.style.width = ''

                // if notification bar exists, move the menu down a bit
                if (notificationBarEl && notificationBarHeight) {
                    gsap.to('[data-top-menu]', {
                        marginTop: 0,
                        duration: .6,
                        ease: 'power2.inOut'
                    })
                }
                
                // restore scroll position after layout has settled
                isRestoringScrollRef.current = true
                
                // ensure menu is visible when closing
                setIsMenuHidden(false)
                
                // update scrolled state based on restored position
                setIsScrolled(scrollPosition > 100)
                
                // update last scroll position before restoring to prevent scroll direction detection issues
                lastScrollYRef.current = scrollPosition
                
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo({ top: scrollPosition, left: 0, behavior: 'auto' })
                        enableFocusOutside()
                        
                        // wait for scroll to complete, then update lastScrollYRef to actual position
                        setTimeout(() => {
                            const actualScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
                            // only update if we're close to target (within 10px), otherwise keep target
                            if (Math.abs(actualScrollY - scrollPosition) < 10) {
                                lastScrollYRef.current = actualScrollY
                            } else {
                                // if not scrolled yet, keep the target position
                                lastScrollYRef.current = scrollPosition
                            }
                        }, 100)
                        
                        // keep flag true longer to prevent scroll handler from processing restoration scroll
                        setTimeout(() => {
                            // check if scroll has reached target position
                            const finalScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
                            const isAtTarget = Math.abs(finalScrollY - scrollPosition) < 5
                            
                            if (isAtTarget) {
                                // scroll has reached target, update and clear flag
                                lastScrollYRef.current = finalScrollY
                                isRestoringScrollRef.current = false
                            } else {
                                // still animating, keep target position in lastScrollYRef and extend flag
                                lastScrollYRef.current = scrollPosition
                                // extend flag for another 200ms
                                setTimeout(() => {
                                    const endScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
                                    lastScrollYRef.current = endScrollY
                                    isRestoringScrollRef.current = false
                                }, 200)
                            }
                        }, 300)
                    })
                })
                
                // remove transparent class
                setTimeout(() => {
                    setIsTransparent(false)
                }, 300)
            }

            setTimeout(() => {
                setIsAnimating(false)
            }, 650)
        }

        return () => {
            document.removeEventListener('keydown', handleMenuTabKey)
        }
	}, [isShown])

    useEffect(() => {
        window.onpageshow = () => {
            if (menuAnimationRef.current) {
                menuAnimationRef.current.progress(0).pause()
                setIsShown(false)
            }
        }
    }, [])

    // initialize scroll tracking
    useEffect(() => {
        const initialScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
        lastScrollYRef.current = initialScrollY
    }, [])

    // handle scroll to hide / show menu
    useEffect(() => {
        const handleScroll = () => {
            // skip scroll handling when restoring scroll position or during hash scroll
            if (isRestoringScrollRef.current || hashScrolledRef.current) return
            
            const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
            const scrollDirection = currentScrollY > lastScrollYRef.current ? 'down' : 'up'
            
            // add scrolled class when past 100px
            setIsScrolled(currentScrollY > 100)
            
            // hide menu when scrolling down past 100px
            if (scrollDirection === 'down' && currentScrollY > 100) {
                setIsMenuHidden(true)
            }
            // show menu when scrolling up or at the top
            else if (scrollDirection === 'up' || currentScrollY <= 100) {
                setIsMenuHidden(false)
            }
            
            lastScrollYRef.current = currentScrollY
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    // handle hash navigation - scroll to element with hash ID
    useEffect(() => {
        const scrollToHash = () => {
            const hash = window.location.hash
            
            if (hash) {
                // Remove the # from the hash
                const id = hash.substring(1)
                
                // Store the full URL with hash for restoration
                const fullUrl = window.location.href
                const urlWithoutHash = window.location.href.split('#')[0]
                
                // Temporarily remove hash from URL to prevent iOS auto-scroll
                window.history.replaceState(null, '', urlWithoutHash)
                
                // Check if mobile (same breakpoint as FsMenuColumn)
                const isMobile = window.innerWidth < 992
                
                // Wait for page to render (300ms delay on mobile, 100ms on desktop)
                setTimeout(() => {
                    const element = document.getElementById(id)
                    
                    if (element) {
                        
                        // Calculate element position
                        const elementPosition = element.getBoundingClientRect().top + window.scrollY
                        const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
                        
                        // Apply 100px offset on mobile
                        const targetPosition = isMobile ? elementPosition - 100 : elementPosition
                        
                        // Check if we're already at the target position (within 10px tolerance)
                        const isAlreadyAtTarget = Math.abs(currentScrollY - targetPosition) < 10
                        
                        // Only scroll if we're not already at the target
                        if (!isAlreadyAtTarget) {
                            // Set flag to prevent scroll handler from interfering
                            hashScrolledRef.current = true
                            
                            if (isMobile) {
                                // On mobile, use window.scrollTo with offset
                                window.scrollTo({
                                    top: targetPosition,
                                    behavior: 'smooth'
                                })
                            } else {
                                // On desktop, use scrollIntoView for better iOS compatibility
                                element.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start'
                                })
                            }
                            
                            // Restore hash in URL after scroll starts
                            setTimeout(() => {
                                window.history.replaceState(null, '', fullUrl)
                            }, 50)
                            
                            // Update scroll tracking refs after a short delay
                            setTimeout(() => {
                                const finalScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
                                lastScrollYRef.current = finalScrollY
                                setIsScrolled(finalScrollY > 100)
                                
                                // Clear flag after scroll completes
                                setTimeout(() => {
                                    hashScrolledRef.current = false
                                }, 500)
                            }, 100)
                        } else {
                            // Restore hash even if we don't scroll
                            window.history.replaceState(null, '', fullUrl)
                        }
                    } else {
                        // Restore hash if element not found
                        window.history.replaceState(null, '', fullUrl)
                    }
                }, isMobile ? 300 : 100)
            }
        }

        // Scroll on initial load
        scrollToHash()

        // Also listen for hash changes (e.g., when clicking anchor links)
        const handleHashChange = () => {
            scrollToHash()
        }

        window.addEventListener('hashchange', handleHashChange)
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange)
        }
    }, [])

    // animations
    useStaggerUp()
    useStaggerScale()
    useStaggerLeft()

    return (
        <>

            {notificationBar?.enabled && (
                <NotificationBar
                    text={notificationBar?.text}
                    enabled={notificationBar?.enabled}
                />
            )}

            <section className={clsx('top-menu', isTransparent && 'top-menu--transparent', isMenuHidden && 'top-menu--hidden', isScrolled && 'top-menu--scrolled')} data-top-menu>
                <div className='container'>
                    <div className='top-menu__flex'>

                        <a
                            href={logo?.url}
                            className='top-menu__logo'
                        >
                            <img
                                src={logo?.src}
                                alt={logo?.alt}
                                width={191}
                                height={78}
                            />
                        </a>

                        <ul className='top-menu__menu'>
                            {menu?.map((item, i) => (
                                <li key={i}>
                                    <Link
                                        href={item?.url}
                                        className='body'
                                        label={{
                                            underline: item?.label
                                        }}
                                    />
                                </li>
                            ))}
                        </ul>

                        <div className='top-menu__right-side'>

                            {!language?.hidden && (
                                <Link
                                    href={language?.active === 'nb' ? language?.noUrl : language?.enUrl}
                                    data-language={language?.active}
                                    className='body'
                                    label={{
                                        underline: language?.active === 'nb' ? 'EN' : 'NO'
                                    }}
                                />
                            )}

                            <a
                                href={search?.url}
                                className='top-menu__search-button'
                            >
                                <img
                                    src={search?.icon}
                                    alt={search?.label}
                                    width={20}
                                    height={20}
                                />
                            </a>

                            <button
                                ref={menuButtonRef}
                                className={clsx(
                                    'top-menu__menu-button body',
                                    isShown && 'top-menu__menu-button--active'
                                )}
                                onClick={openCloseMenu}
                                aria-label={isShown ? menuButton?.close : menuButton?.open}
                                type='button'
                            >
                                {isShown ? menuButton?.close : menuButton?.open}
                                <span className='top-menu__menu-button__icon'>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </span>
                            </button>

                        </div>

                    </div>
                </div>
            </section>
            

            <section
                className={clsx('fs-menu', isShown && 'fs-menu--open')}
                data-fs-menu
            >
                <div className='container'>
                    
                    <div className='fs-menu__grid'>
                        {fsMenu?.map((item, i) => (
                            <FsMenuColumn
                                key={i}
                                title={item.title}
                                links={item.links}
                                icon={fsMenuIcon}
                            />
                        ))}
                    </div>

                    <div className='fs-menu__last-link'>
                        <Link
                            href={fsMenuLastLink?.url}
                            className='body'
                            label={{
                                underline: fsMenuLastLink?.label
                            }}
                        />
                    </div>

                </div>
            </section>
        </>
    )
}

interface FsMenuColumnProps {
    title: {
        label: string
        url?: string
    }
    links: {
        label: string
        url: string
    }[]
    icon?: string
}

function FsMenuColumn({
    title,
    links,
    icon = '/svg/ux/chevron-down.svg'
}: FsMenuColumnProps) {

    const [isMobile, setIsMobile] = useState(false)
    const [isActive, setIsActive] = useState(false)

	const toggle = () => {
		setIsActive((prev) => !prev)
	}

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 992)
        }

        checkMobile()

        window.addEventListener('resize', checkMobile)

        return () => {
            window.removeEventListener('resize', checkMobile)
        }
    }, [])

    return (
        <div className='fs-menu__column' data-fs-menu-column>
            
            {title?.label && (
                isMobile ? (
                    <div className='fs-menu__column__title-wrapper'>
                        <button
                            className={clsx(
                                'fs-menu__column__sub-menu',
                                isActive && 'fs-menu__column__sub-menu--active'
                            )}
                            onClick={toggle}
                        >
                            <img
                                src={icon}
                                alt=''
                                width={22}
                                height={22}
                            />
                        </button>

                        {title.url ? (
                            <Link
                                href={title.url}
                                className='fs-menu__column__title h3'
                                label={{
                                    underline: title.label
                                }}
                            />
                        ) : (
                            <p className='fs-menu__column__title h3'>
                                {title.label}
                            </p>
                        )}
                    </div>
                ) : (
                    title.url ? (
                        <Link
                            href={title.url}
                            className='fs-menu__column__title h2'
                            label={{
                                underline: title.label
                            }}
                        />
                    ) : (
                        <p className='fs-menu__column__title h2'>
                            {title.label}
                        </p>
                    )
                )
            )}

            <div className={clsx(
                'fs-menu__column__content',
                isActive && 'fs-menu__column__content--active'
            )}>
                <div>
                    <ul className='fs-menu__column__list'>
                        {links?.map((item, i) => (
                            <li key={i}>
                                <Link
                                    href={item?.url}
                                    className='body'
                                    label={{
                                        underline: item?.label
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

        </div>
    )
}